import React, { useEffect, useRef, useState } from "react"
import { Button } from "reactstrap"
import { get } from "helpers/api_helper"

const reasons = {
  recordNotFound: "The Sportya transaction no longer exists.", sessionExpired: "Your Sportya session expired. Sign in again.", forbidden: "Your Sportya account cannot access this transaction.",
  missingReference: "No Stripe payment reference is recorded.", notConfigured: "Stripe verification is not configured.",
  notFound: "Payment not found in the configured Stripe accounts or modes.", accessDenied: "Stripe access does not allow this verification.",
  timeout: "Stripe did not respond in time. Try again.", ambiguous: "The payment matches more than one Stripe context. The link cannot be verified.",
  unverified: "The Stripe account or payment could not be verified.", providerUnavailable: "Stripe could not be reached. Try again.",
}
const isPaymentReference = value => /^(pi|ch)_[a-zA-Z0-9]{1,250}$/.test(value || "")
const safeLink = data => {
  if (data?.status !== "verified" || !isPaymentReference(data.reference) || !/^acct_[a-zA-Z0-9]+$/.test(data.accountId || "") || !["live", "test"].includes(data.mode)) return null
  const expected = `https://dashboard.stripe.com/${data.mode === "test" ? "test/" : ""}${data.accountId}/payments/${data.reference}`
  return data.url === expected ? expected : null
}

export default function StripePaymentLink({ payment }) {
  const [state, setState] = useState({}), [copied, setCopied] = useState("")
  const requestVersion = useRef(0)
  useEffect(() => { requestVersion.current += 1; setState({}); setCopied(""); return () => { requestVersion.current += 1 } }, [payment.id])
  const resolve = async () => {
    const version = ++requestVersion.current
    setState({ id: payment.id, loading: true }); setCopied("")
    try {
      const response = await get(`/admin/payments/${encodeURIComponent(payment.id)}/stripe-link`, { timeout: 15000 })
      if (version === requestVersion.current) setState({ id: payment.id, data: response?.data || { reason: "unverified" } })
    } catch (error) {
      const reason = ({ 404: "recordNotFound", 401: "sessionExpired", 403: "forbidden" })[error?.response?.status]
        || (["ECONNABORTED", "ETIMEDOUT"].includes(error?.code) ? "timeout" : "providerUnavailable")
      if (version === requestVersion.current) setState({ id: payment.id, data: { reason } })
    }
  }
  const current = state.id === payment.id ? state : {}
  const url = safeLink(current.data)
  const reference = current.data?.reference || payment.processorPaymentId
  const safeReference = isPaymentReference(reference) ? reference : null
  if (!payment.stripeLinkAvailable && payment.paymentType !== "creditCard") return null
  return <div className="payments-stripe" aria-label="Stripe payment">
    {!url && <Button color="link" size="sm" className="payments-stripe-action p-0" disabled={current.loading} onClick={resolve}>{current.loading ? "Verifying Stripe…" : current.data ? "Retry Stripe" : "View in Stripe"}</Button>}
    {url && <><a className="payments-stripe-action" href={url} target="_blank" rel="noopener noreferrer">Open in Stripe</a><small className="d-block">{current.data.context} · {current.data.mode === "test" ? "Test" : "Live"} · {current.data.accountId}</small></>}
    {current.data && !url && <small role="status" className="d-block">{reasons[current.data.reason] || reasons.unverified}</small>}
    {current.data && safeReference && <div><span className="payments-reference">{safeReference}</span> <Button color="link" size="sm" className="payments-stripe-action p-0" onClick={async () => {
      try { await navigator.clipboard.writeText(safeReference); setCopied("Copied") } catch { setCopied("Copy unavailable. Select the reference to copy it.") }
    }}>Copy ID</Button><small role="status" className="d-block">{copied}</small></div>}
  </div>
}
